import {Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {Observable, of, Subject, Subscription} from "rxjs";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {WarningsEnum} from "../../../shared/warnings/warnings.enum";
import {InputMessagesService} from "../../../shared/input/service/input-messages.service";
import {InputDropdownComponent} from "../../../shared/input/input-dropdown/input-dropdown.component";
import {
  SupplyingPackingPlantBaseModel
} from "../../../api/ms-specialorders/contracts/supplying-packing-plant-base.model";
import {AbstractControl, ValidatorFn} from "@angular/forms";
import {MandatoryMessageTypeEnum} from "../../../shared/input/service/mandatory-message-type.enum";
import {InputDisplayModeEnum} from "../../../shared/input/input-display-mode.enum";
import {PncTypeEnum} from "../../models/pnc-type-enum/pnc-type.enum";
import {objectsValidator} from "../../../shared/input/input-validators";
import {TypeaheadHelperService} from "../../../shared/services/typeahead-helper.service";
import {ShipToPartyBaseModel} from "../../../api/ms-specialorders/contracts/ship-to-party-base.model";

@Component({
    selector: 'app-part-number-changes-change-plant-popup',
    templateUrl: './part-number-changes-change-plant-popup.component.html',
    styleUrls: ['./part-number-changes-change-plant-popup.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PartNumberChangesChangePlantPopupComponent implements OnInit {

  @Input() public initialData: any;

  @Input() primaryActionEnabled = false;
  @Input() secondaryActionEnabled = true;

  @Input() isLoading: Subject<boolean>;

  @Output() primaryActionEvent = new EventEmitter<void>();
  @Output() secondaryActionEvent = new EventEmitter<void>();
  @Output() closeEvent = new EventEmitter<void>();

  public loading = false;

  private subscriptions = new Subscription();

  @ViewChild('supplyingPackingPlantInput') supplyingPackingPlantInput: InputDropdownComponent;

  public supplyingPackingPlantInputMessages = new Map<WarningsEnum, string[]>();

  private credentials: any = null;
  private pncType = PncTypeEnum.UNKNOWN;
  public WarningsEnum = WarningsEnum;
  /** Inputs validators */
  validators: { [key: string]: ValidatorFn[] } = {
    partner: [objectsValidator],
    shipToParty: [objectsValidator],
    supplyingPackingPlant: [objectsValidator]
  };

  public isChecked = false;

  private isValid = false;
  private supplyingPackingPlantOptions: SupplyingPackingPlantBaseModel[] = [];
  private solutionSupplyingPackingPlant: SupplyingPackingPlantBaseModel;

  constructor(
      public activeModal: NgbActiveModal,
      private inputMessagesService: InputMessagesService,
      public typeahead: TypeaheadHelperService,
  ) {}

  get disablePrimaryButton(): boolean {
    return !this.isChecked || this.loading || !this.isValid;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    if (this.isLoading != null) {
      this.subscriptions.add(
          this.isLoading.subscribe((loading) => {
            setTimeout(() => {
              this.loading = loading;
            });
          }));
    }

    this.supplyingPackingPlantOptions = this.initialData?.alternativeSupplyingPackingPlants;
  }

  check() {
    this.isChecked = !this.isChecked;
  }

  clickPrimaryButton(): void {
    this.primaryActionEvent.emit();
    this.activeModal.close(this.solutionSupplyingPackingPlant);
  }

  clickSecondaryButton(): void {
    this.secondaryActionEvent.emit();
    this.activeModal.dismiss();
  }

  clickCloseButton(): void {
    this.closeEvent.emit();
    this.activeModal.dismiss();
  }

  /**
   * Manages the supplying packing plant input status changes, adding errors and warnings as needed
   */
  manageSupplyingPackingPlantInputStateChange(plant: AbstractControl): void {
    this.inputMessagesService.mandatoryWarnings(plant,
        this.supplyingPackingPlantInputMessages,
        MandatoryMessageTypeEnum.CREATE);
    this.inputMessagesService.invalidInputValueError(plant, this.supplyingPackingPlantInputMessages,
        this.supplyingPackingPlantInput?.label);
    this.supplyingPackingPlantInputMessages = new Map(this.supplyingPackingPlantInputMessages);
  }


  setSupplyingPackingPlantValue(plant: SupplyingPackingPlantBaseModel): void {
    this.isValid = plant != null && plant.active;
    this.solutionSupplyingPackingPlant = plant;
  }

  isViewMode(): boolean {
    let viewMode = false;

    if (this.credentials != null) {
      viewMode = this.credentials.displayMode === InputDisplayModeEnum.VIEW;
    }
    return viewMode;
  }

  get hasAlternateBackground(): boolean {
    return this.pncType === PncTypeEnum.PARTNER || this.pncType === PncTypeEnum.AI_CHANGE;
  }

  searchSupplyingPackingPlants = (term: string): Observable<ShipToPartyBaseModel[]> => {
    if (term != null && term.length > 0) {
      return of(this.supplyingPackingPlantOptions.filter((plant) => (`${plant.code} - ${plant.name}`)
          .toLowerCase()
          .indexOf(term.toLowerCase()) > -1));
    }
    return of(this.supplyingPackingPlantOptions);
  };
}
